import { InvalidPlaybackQueueSnapshotError, } from '../ports/playback-event-store.js';
import { assembleQueueState, chunkRange, isValidQueueMeta, } from '../playback/playback-queue-chunks.js';
import { META_STORE, openPlayerDatabase } from './idb/database.js';
import { idbRequest, idbTransactionDone } from './idb/request.js';
/**
 * Own keys in the existing meta store, so no new database and no schema version bump:
 * `<prefix>:meta` and `<prefix>:chunk:<n>` (1,000 events each). `<prefix>` alone is the old
 * single-key queue; it's read once, converted by the next save and deleted in that save.
 */
const DEFAULT_PREFIX = 'playbackEvents';
export class IndexedDbPlaybackEventStore {
    openDb;
    keyPrefix;
    dbPromise = null;
    legacyPresent = false;
    constructor(openDb = openPlayerDatabase, keyPrefix = DEFAULT_PREFIX) {
        this.openDb = openDb;
        this.keyPrefix = keyPrefix;
    }
    db() {
        if (!this.dbPromise)
            this.dbPromise = this.openDb();
        return this.dbPromise;
    }
    metaKey() {
        return `${this.keyPrefix}:meta`;
    }
    chunkKey(index) {
        return `${this.keyPrefix}:chunk:${index}`;
    }
    async load() {
        const db = await this.db();
        const metaRecord = await idbRequest(db.transaction(META_STORE, 'readonly').objectStore(META_STORE).get(this.metaKey()));
        const meta = metaRecord ? metaRecord.value : null;
        if (meta !== null && meta !== undefined) {
            const range = isValidQueueMeta(meta)
                ? chunkRange(meta.headSeq, meta.nextSeq)
                : { first: 0, last: -1 };
            const indexes = [];
            for (let index = range.first; index <= range.last; index += 1)
                indexes.push(index);
            const store = db.transaction(META_STORE, 'readonly').objectStore(META_STORE);
            const records = await Promise.all(indexes.map((index) => idbRequest(store.get(this.chunkKey(index)))));
            const byIndex = new Map();
            indexes.forEach((index, n) => {
                const record = records[n];
                byIndex.set(index, record ? record.value : undefined);
            });
            return assembleQueueState(meta, (index) => byIndex.get(index));
        }
        const record = await idbRequest(db.transaction(META_STORE, 'readonly').objectStore(META_STORE).get(this.keyPrefix));
        const value = record ? record.value : null;
        if (value === null || value === undefined)
            return [];
        this.legacyPresent = true;
        if (Array.isArray(value))
            return value;
        const snapshot = value;
        if (snapshot.version !== 1 ||
            typeof snapshot.queueId !== 'string' ||
            !Array.isArray(snapshot.events) ||
            !Number.isSafeInteger(snapshot.droppedEvents) ||
            snapshot.droppedEvents < 0 ||
            !Number.isSafeInteger(snapshot.acknowledgedDroppedEvents) ||
            snapshot.acknowledgedDroppedEvents < 0 ||
            snapshot.acknowledgedDroppedEvents > snapshot.droppedEvents)
            throw new InvalidPlaybackQueueSnapshotError('Invalid playback queue snapshot', Array.isArray(snapshot.events) ? snapshot.events.length : 0);
        return snapshot;
    }
    /** One transaction: delete gone chunks, write changed chunks and the meta, drop the old key. */
    async save(write) {
        const db = await this.db();
        const tx = db.transaction(META_STORE, 'readwrite');
        const done = idbTransactionDone(tx);
        const store = tx.objectStore(META_STORE);
        for (const index of write.deleteChunks)
            store.delete(this.chunkKey(index));
        for (const chunk of write.chunks) {
            store.put({ key: this.chunkKey(chunk.index), value: chunk.events });
        }
        store.put({ key: this.metaKey(), value: write.meta });
        if (this.legacyPresent)
            store.delete(this.keyPrefix);
        await done;
        this.legacyPresent = false;
    }
}
//# sourceMappingURL=indexed-db-playback-event-store.js.map